import { Box, Typography, CircularProgress, useTheme, IconButton, Collapse, Tooltip } from '@mui/material'
import React, { memo, useState } from 'react'
import { toast } from 'react-toastify'

import DepartmentOrBranchPermission from './DepartmentOrBranchPermission'
import CustomSwitch from '../../common/CustomSwitch'
import CustomTabs from '../../common/CustomTabs'
import { Icons } from '../../../utils/utilities'
import api from '../../../service/api'

const tabs = [
    { id: "deparmtent", value: "departments", label: "Departments", icon: Icons.default.Building1 },
    { id: "beanches", value: "branches", label: "Branches", icon: Icons.default.Branches }
]

const FeatureInstituteCard = memo( ( { record, refresh, departmentsAndBranches, featureKey } ) => {

    const [manageOverrides, setManageOverrides] = useState( false )
    const [tabValue, setTabValue] = useState( 'departments' )
    const [updating, setUpdating] = useState( false )

    const { border, palette, layout } = useTheme()

    const updateInstituteFeatureDefaultPermission = async event => {
        try {
            setUpdating( true )
            const payload = {
                feature_key: featureKey,
                institute_id: record.code,
                is_enabled: event.target.checked,
                update_type: "default_permission"
            }

            await api.featurePermissions.updatePermissions( featureKey, payload )
            await refresh( false )
        } catch ( err ) {
            console.log( err )
            toast( err?.response?.data?.message || "Something went wrong while updating the override permission." )
        } finally {
            setUpdating( false )
        }
    }

    const overridesExists = Object.values( record.department_overrides ).includes( !record.default_permission ) || Object.values( record.branch_overrides ).includes( !record.default_permission )

    return (
        <Box
            border={border[1]}
            borderRadius={layout.borderRadius}
            bgcolor={palette.background.paper}
        >
            <Box
                onClick={( event ) => { event.stopPropagation(); setManageOverrides( prev => !prev ) }}
                sx={{
                    cursor: "pointer",
                    ":hover": {
                        bgcolor: palette.action.hover
                    }
                }}
                padding="10px"
                display="flex"
                justifyContent="space-between"
                alignItems="center"
            >
                <Box>
                    <Typography variant="subtitle2" component="p" textTransform="capitalize" fontSize="14px" display="block">{record.name?.toLowerCase()}</Typography>
                    <Typography variant="caption" color="textSecondary">{record.code}</Typography>
                </Box>
                <Box flexShrink={0} display="flex" flexDirection="column" alignItems="flex-end">
                    <Box display="flex" alignItems="center" gap="10px">
                        <Box onClick={( event ) => { event.stopPropagation() }} >
                            {updating ? <CircularProgress size={26} color='greyed' /> : <CustomSwitch size="small" onChange={updateInstituteFeatureDefaultPermission} color="success" checked={Boolean( record.default_permission )} />}
                        </Box>
                        <Tooltip title="Manage Department and Branch permissions">
                            <IconButton size='small' onClick={( event ) => { event.stopPropagation(); setManageOverrides( prev => !prev ) }} color='greyed' >{Icons.default.SettingsOutlined}</IconButton>
                        </Tooltip>
                    </Box>
                    {overridesExists && <Typography variant="caption" color="warning" display="flex" gap="5px" alignItems="center"> {Icons.default.Warning} Overrides exists.</Typography>}
                </Box>
            </Box>
            <Collapse in={manageOverrides}>
                <Box borderTop={border[1]} padding="10px" >
                    <Box overflow="clip" borderRadius={layout.borderRadius} bgcolor={palette.contentBg} maxWidth={{ md: "400px", xs: "initial" }}>
                        <CustomTabs variant='stretched' tabs={tabs} value={tabValue} handleTabChange={setTabValue} />
                        <Box maxHeight="200px" display="flex" flexDirection="column" gap="5px" padding="10px" marginTop="5px" className="slim-custom-scrollbar" overflow="auto">
                            {departmentsAndBranches?.[record.code]?.[tabValue]?.map( group => {
                                let permission_granted, id, key
                                if ( tabValue === "branches" ) {
                                    permission_granted = record.branch_overrides[group.course_branch_assignment_id] ?? record.default_permission
                                    key = `${group.course_branch_short_name}-${group.course_branch_assignment_id}`
                                    id = group.course_branch_assignment_id
                                } else {
                                    permission_granted = record.department_overrides[group.department_id] ?? record.default_permission
                                    key = `${group.department_name}-${group.department_id}`
                                    id = group.department_id
                                }
                                return ( <DepartmentOrBranchPermission permission={permission_granted} feature_key={featureKey} refresh={refresh} group={group} record={record} tabValue={tabValue} id={id} key={key} /> )
                            } )}
                        </Box>
                    </Box>
                </Box>
            </Collapse>
        </Box>
    )
} )

export default FeatureInstituteCard